/**
 * NoteAudioPlayer — sample-accurate slice playback for Music Note
 * elements (and any other gameplay sound that needs precise timing).
 *
 * `SceneAudio` wraps HTMLAudioElement, which is great for looping
 * background tracks and chunky one-shot SFX — but the browser is
 * allowed to round playback positions to ~50ms boundaries and a fresh
 * `play()` carries seek latency. That makes it impossible to chain
 * several elements that each play a synced 1-second slice of one MP3
 * (Mario-Wonder note-tile feel) without audible drift / gaps.
 *
 * Web Audio's `AudioBufferSourceNode.start(when, offset, duration)`
 * solves both problems: each slice is scheduled on the audio clock
 * and starts at an exact sample, with no seek cost. We lazily decode
 * each clip into a cached AudioBuffer the first time it's needed, so
 * later notes referencing the same MP3 just schedule from RAM.
 *
 * Autoplay policy: an AudioContext created before any user gesture
 * starts `suspended` on most browsers. We arm a one-shot
 * pointer/key/touch listener on `window` that resumes the context the
 * first time the player taps / presses a key — identical pattern to
 * SceneAudio's `_armGesture`. Once resumed, every `playSlice` lands
 * immediately.
 *
 * Built-in fade-out: `playSlice(..., fadeOutSec)` schedules a smooth
 * linear ramp over the LAST `fadeOutSec` of the playback so a "finale"
 * slice can end without an audible click. The fade is baked into the
 * source's gain envelope — once started, it runs entirely on the audio
 * thread, so frame-rate hiccups can never cause a stutter.
 *
 * Cancellable playback: `playSlice` returns a `handle` synchronously
 * (even if the buffer is still decoding) which `stopHandle(handle,
 * fadeMs)` can fade out and stop early. Used on respawn / scene
 * teardown and when an older tail is still ringing as the player
 * re-runs the same tune.
 *
 * Best-effort throughout: decode failures, an unavailable Web Audio
 * implementation, or a locked context never break gameplay — the
 * sound just stays silent and the error is logged once.
 */

export class NoteAudioPlayer {
    /** @type {AudioContext|null} */
    _ctx = null;
    /** @type {GainNode|null} */
    _master = null;
    /** Cached decoded buffers, keyed by src (data URL or path). */
    _buffers = new Map();
    /** Promises in flight while a buffer decodes — dedup parallel asks. */
    _pending = new Map();
    /** @type {(() => void)|null} */
    _gestureHandler = null;
    /** Live source nodes mapped to their per-source GainNode, so
     *  `silenceAll()` and `destroy()` can fade + stop EVERY live
     *  source — even the ones whose handle the caller discarded
     *  (fire-and-forget solo notes, one-shots, sound-zone SFX).
     *  A Map (vs the previous Set) is what makes the universal
     *  silence sweep possible: without the gain reference there's
     *  no way to ramp a source down before stopping it, which
     *  produces the click + buffered-tail bleed-through we saw
     *  when a Stop zone fired during a long fade-out tail. */
    _sources = new Map();
    /** Master volume (0..1). Applied through the master GainNode. */
    _volume = 1;
    _destroyed = false;

    /**
     * @param {object} [opts]
     * @param {number} [opts.volume=1]  Master gain applied to every slice.
     */
    constructor(opts = {}) {
        if (Number.isFinite(opts.volume)) this._volume = clamp01(opts.volume);
    }

    /** Live setter for the master gain. Safe to call before init. */
    setVolume(v) {
        this._volume = clamp01(v);
        if (this._master) {
            try { this._master.gain.value = this._volume; } catch (_) {}
        }
    }

    /**
     * Lazy-init the AudioContext. The context is `suspended` until a
     * user gesture resumes it; we arm that listener here so the first
     * tap / key takes care of it without callers needing to know.
     *
     * Returns null when Web Audio is unavailable (very old browsers).
     */
    _ensureCtx() {
        if (this._destroyed) return null;
        if (this._ctx) return this._ctx;
        try {
            const Ctor = (typeof window !== 'undefined')
                ? (window.AudioContext || window.webkitAudioContext)
                : null;
            if (!Ctor) return null;
            this._ctx = new Ctor();
            this._master = this._ctx.createGain();
            this._master.gain.value = this._volume;
            this._master.connect(this._ctx.destination);
            if (this._ctx.state === 'suspended') this._armGesture();
            return this._ctx;
        } catch (err) {
            console.warn('[NoteAudioPlayer] AudioContext init failed:', err?.message || err);
            return null;
        }
    }

    /**
     * Fetch + decode the clip at `src` (data URL or HTTP path) into an
     * AudioBuffer, cached for subsequent calls. Returns null on failure
     * so callers can short-circuit silently.
     *
     * @param {string} src
     * @returns {Promise<AudioBuffer|null>}
     */
    async loadBuffer(src) {
        if (!src) return null;
        const cached = this._buffers.get(src);
        if (cached) return cached;
        const inflight = this._pending.get(src);
        if (inflight) return inflight;

        const ctx = this._ensureCtx();
        if (!ctx) return null;

        const p = (async () => {
            try {
                const res = await fetch(src);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const bytes = await res.arrayBuffer();
                // Use the callback form when available for Safari, but
                // promise-form first since it's universal in modern
                // browsers.
                const buf = await new Promise((resolve, reject) => {
                    try {
                        const maybe = ctx.decodeAudioData(bytes, resolve, reject);
                        if (maybe && typeof maybe.then === 'function') maybe.then(resolve, reject);
                    } catch (e) { reject(e); }
                });
                if (this._destroyed) return null;
                this._buffers.set(src, buf);
                return buf;
            } catch (err) {
                console.warn('[NoteAudioPlayer] decode failed for', src?.slice(0, 80), err?.message || err);
                return null;
            } finally {
                this._pending.delete(src);
            }
        })();
        this._pending.set(src, p);
        return p;
    }

    /**
     * Eagerly decode clips so the first contact doesn't stutter while
     * the fetch + decode happens. Best-effort: any failure is silent.
     * @param {Iterable<string>} srcs
     */
    async preload(srcs) {
        const out = [];
        for (const src of srcs || []) {
            if (!src) continue;
            out.push(this.loadBuffer(src).catch(() => null));
        }
        return Promise.all(out);
    }

    /**
     * Schedule a precise slice of the clip at `src`. Returns a `handle`
     * SYNCHRONOUSLY (even though the buffer decode may still be in
     * flight) that can be passed to `stopHandle()` to fade and stop the
     * playback early. Calling `stopHandle()` before the source has
     * actually started cancels the upcoming play.
     *
     * `offsetSec` clamps to [0, buffer.duration]. `durationSec === 0`
     * (or undefined) plays from `offsetSec` to the end of the buffer.
     * `volume` is multiplied with the master gain. Overlapping calls
     * all schedule their own source so multiple notes can sing at once.
     * `fadeOutSec > 0` applies a linear ramp to silence over the LAST
     * `fadeOutSec` of the scheduled playback (clamped to the playback
     * length) — the source still stops at the end.
     *
     * `handle.ended` flips to true when the audio thread fires
     * `onended` (or the safety timeout below disconnects it), so the
     * caller can poll the handle to know whether a playback is still
     * audibly running without owning its own timer.
     *
     * Cancellation is decode-aware: if `stopHandle()` is called BEFORE
     * the buffer has finished decoding and the source has started, we
     * record a `_pendingStopMs` on the handle so the async path
     * applies it the instant playback begins. That way a fast triggered
     * slice still produces a brief audible attack instead of being
     * skipped entirely — the previous "stop before start" path would
     * mark `stopped=true` and the slice would never play at all.
     *
     * `whenSec` schedules the slice to start at a specific
     * AudioContext time (in seconds). Pass a value > `now()` to
     * QUEUE the slice for future playback — this is how
     * PlatformerNotes builds a "play at slice tempo" tune from a
     * burst of fast triggers: each new slice is scheduled to start
     * exactly when the previous one ends, so the song plays out at
     * its natural tempo even if the player runs through every note
     * in the same frame. Omit (or pass 0 / null) to play
     * immediately, like before. Out-of-range values clamp to `now`
     * silently. The end-of-playback fade is relative to the
     * scheduled start, not wall-clock time.
     *
     * @param {string} src
     * @param {number} offsetSec
     * @param {number} [durationSec=0]
     * @param {number} [volume=1]
     * @param {number} [fadeOutSec=0]
     * @param {number} [whenSec=0]  AudioContext time (sec) to start at; 0/falsy = now.
     * @returns {{stopped:boolean, ended:boolean, source:AudioBufferSourceNode|null, gain:GainNode|null, _pendingStopMs:number}}
     *          A stop handle. Read-only from the caller's POV.
     */
    playSlice(src, offsetSec, durationSec = 0, volume = 1, fadeOutSec = 0, whenSec = 0) {
        const handle = { stopped: false, ended: false, source: null, gain: null, _pendingStopMs: 0 };
        if (this._destroyed || !src) { handle.stopped = true; handle.ended = true; return handle; }
        const ctx = this._ensureCtx();
        if (!ctx) { handle.stopped = true; handle.ended = true; return handle; }

        (async () => {
            const buf = await this.loadBuffer(src);
            if (!buf || this._destroyed || handle.stopped) { handle.ended = true; return; }

            const dur = Math.max(0, Number(buf.duration) || 0);
            const off = clamp(Number(offsetSec) || 0, 0, dur);
            const want = Math.max(0, Number(durationSec) || 0);
            const len = want > 0 ? Math.min(want, dur - off) : Math.max(0, dur - off);
            if (len <= 0) { handle.ended = true; return; }

            // Try to resume a suspended context inline — most browsers
            // accept this when the call is on a user-gesture stack frame.
            if (ctx.state === 'suspended') {
                try { ctx.resume(); } catch (_) {}
            }

            try {
                const now = ctx.currentTime;
                // Schedule START time. If the caller asked for a
                // future time we honour it (the audio thread will
                // hold the source idle until then); a past time or
                // 0 collapses to "play now".
                const requested = Number(whenSec) || 0;
                const start = requested > now ? requested : now;
                const src1 = ctx.createBufferSource();
                src1.buffer = buf;
                const g = ctx.createGain();
                const v = clamp01(volume);
                g.gain.value = v;
                src1.connect(g);
                g.connect(this._master);
                // End-of-playback fade is scheduled relative to the
                // SCHEDULED start time so a queued slice fades at the
                // right moment in audio-clock land (not when it was
                // booked). Once running, the audio thread owns the
                // envelope so frame drops can't make it stutter.
                const fade = Math.max(0, Number(fadeOutSec) || 0);
                if (fade > 0) {
                    const fadeLen = Math.min(fade, len);
                    g.gain.setValueAtTime(v, start + (len - fadeLen));
                    g.gain.linearRampToValueAtTime(0, start + len);
                }
                src1.start(start, off, len);
                this._sources.set(src1, g);
                handle.source = src1;
                handle.gain = g;
                const cleanup = () => {
                    this._sources.delete(src1);
                    handle.ended = true;
                    try { src1.disconnect(); g.disconnect(); } catch (_) {}
                };
                src1.onended = cleanup;
                // Hard ceiling so a clamped/queued slice never leaks
                // if onended is skipped — include the wait time the
                // source is idling before it actually plays.
                const waitMs = Math.max(0, (start - now) * 1000);
                setTimeout(cleanup, waitMs + (len + 0.25) * 1000);
                // Honour any stop request that came in while we were
                // decoding. We give the slice a tiny audible moment
                // (the requested fade) so a fast pass-through still
                // produces sound instead of being silently dropped.
                if (handle._pendingStopMs > 0) {
                    this.stopHandle(handle, handle._pendingStopMs);
                }
            } catch (err) {
                console.warn('[NoteAudioPlayer] play failed:', err?.message || err);
                handle.ended = true;
            }
        })();
        return handle;
    }

    /**
     * AudioContext current time (seconds), or 0 if the context
     * hasn't been initialised yet. Exposed so callers building
     * scheduled queues (PlatformerNotes' "play at slice tempo"
     * model) can read the same clock the slice scheduler uses.
     */
    now() {
        return this._ctx ? this._ctx.currentTime : 0;
    }

    /**
     * Fade `handle` to silence over `fadeMs` and stop the source.
     *
     * Decode-aware: when called BEFORE the async decode has finished
     * and the source has been started, the request is parked on
     * `handle._pendingStopMs` and the async path applies it the
     * instant the source starts. That guarantees a fast trigger
     * still produces a brief audible attack (= the requested fade
     * window), rather than being silently cancelled the way the
     * previous `handle.stopped = true` short-circuit did.
     *
     * Idempotent: a second call on an already-stopping handle just
     * upgrades the pending fade if longer. A small minimum (~5 ms)
     * is used even for "instant" stops to avoid the click that a
     * bare `source.stop()` produces mid-waveform.
     *
     * Implementation notes:
     *   - Uses `cancelAndHoldAtTime(now)` when available (Chrome /
     *     Firefox) so the fade ALWAYS starts from the gain's actual
     *     current value, even when an existing automation (e.g. the
     *     5-second tail's linear fade-out) is in flight. The
     *     previous `gain.value` + `cancelScheduledValues` + new
     *     `setValueAtTime` pattern was racy in Chrome because
     *     `.value` doesn't reliably reflect mid-automation values —
     *     a Stop zone firing during a tail fade would snap the gain
     *     back to v and ramp from there, producing the ~1–2 s of
     *     residual sound bleed the user reported.
     *   - After the fade lands, force-disconnects the source from
     *     the graph. The audio thread may have already buffered the
     *     next ~50 ms of samples when the fade is very short
     *     (e.g. 10 ms), and `src.stop(t)` only schedules the stop
     *     — a hard disconnect is the only way to GUARANTEE no
     *     residual bleed-through, especially for the long
     *     tail-fade slice case where the gain ramp races against
     *     the previously-scheduled tail envelope.
     *
     * @param {{stopped:boolean, source:AudioBufferSourceNode|null, gain:GainNode|null, _pendingStopMs?:number}|null} handle
     * @param {number} [fadeMs=30]  Linear fade duration in ms.
     */
    stopHandle(handle, fadeMs = 30) {
        if (!handle || handle.stopped) return;
        const src = handle.source, gain = handle.gain, ctx = this._ctx;
        const ms = Math.max(0, Number(fadeMs) || 0);
        if (!src || !gain || !ctx) {
            // Source hasn't started yet (decode in flight). Record
            // the longest pending fade so the async path applies it
            // once playback begins — don't mark `stopped` here, or
            // the source will never be created at all.
            handle._pendingStopMs = Math.max(handle._pendingStopMs || 0, ms || 30);
            return;
        }
        handle.stopped = true;
        const now = ctx.currentTime;
        const fade = Math.max(0.005, ms / 1000);
        _rampGainToZero(gain, ctx, fade);
        try { src.stop(now + fade + 0.02); } catch (_) {
            try { src.stop(0); } catch (__) {}
        }
        // Belt-and-suspenders disconnect: even if the audio thread
        // has buffered samples past the scheduled stop, removing
        // the node from the graph mutes it instantly. Runs slightly
        // after the gain reaches zero so the fade is audible.
        const cleanupAt = (fade + 0.05) * 1000;
        setTimeout(() => {
            try { src.disconnect(); } catch (_) {}
            try { gain.disconnect(); } catch (_) {}
            this._sources.delete(src);
            handle.ended = true;
        }, cleanupAt);
    }

    /** Play the full clip from the start. Convenience wrapper. */
    playOneShot(src, volume = 1) {
        return this.playSlice(src, 0, 0, volume);
    }

    /**
     * Fade out + stop + disconnect EVERY live source the player is
     * managing — slices, one-shots, queued (not-yet-started)
     * slices, and long-running music tracks alike. The Music Stop
     * zone calls this so a level designer can rely on it leaving
     * the level genuinely silent regardless of which kind of source
     * is currently ringing (including fire-and-forget solo notes
     * and sound-zone SFX whose handle the caller discarded).
     *
     * The fade lands per-source via the same `cancelAndHoldAtTime`
     * pattern `stopHandle` uses, so an in-flight 5-second tail
     * fade-out is correctly captured at its current value and
     * ramped down from there instead of snapping back to v. After
     * the fade the source is hard-disconnected from the graph so
     * even buffered samples can't bleed through.
     *
     * @param {number} [fadeMs=30]
     */
    silenceAll(fadeMs = 30) {
        const ctx = this._ctx;
        if (!ctx) return;
        const ms = Math.max(0, Number(fadeMs) || 0);
        const fade = Math.max(0.005, ms / 1000);
        const now = ctx.currentTime;
        // Snapshot — disconnect callbacks below mutate _sources.
        const entries = Array.from(this._sources.entries());
        for (const [, gain] of entries) {
            _rampGainToZero(gain, ctx, fade);
        }
        for (const [src] of entries) {
            try { src.stop(now + fade + 0.02); } catch (_) {
                try { src.stop(0); } catch (__) {}
            }
        }
        const cleanupAt = (fade + 0.05) * 1000;
        setTimeout(() => {
            for (const [src, gain] of entries) {
                try { src.disconnect(); } catch (_) {}
                try { gain.disconnect(); } catch (_) {}
                this._sources.delete(src);
            }
        }, cleanupAt);
    }

    /**
     * Play `src` as a long-running music track. Returns a handle in
     * the same shape `playSlice` does so the caller can fade it out
     * with `stopHandle(handle, fadeMs)` at any time.
     *
     * Differences vs. `playSlice`:
     *   - Plays the WHOLE buffer (no offset / slice clamping).
     *   - Optional `loop` flag so the track repeats seamlessly until
     *     the caller cuts it (background music style).
     *   - Optional `fadeInSec` ramps the gain up from 0 to the
     *     requested volume over that many seconds, baked into the
     *     same gain envelope as the fade-out so the audio thread
     *     owns both ends. Useful for cross-fading from a previous
     *     music track without an audible pop.
     *
     * Pending-stop semantics are inherited verbatim: a `stopHandle`
     * called before the buffer finishes decoding parks on
     * `_pendingStopMs` and applies the instant playback begins, so
     * even a quickly-cancelled music start still produces a brief
     * audible attack.
     *
     * @param {string} src
     * @param {object} [opts]
     * @param {number} [opts.volume=1]
     * @param {number} [opts.fadeInSec=0]
     * @param {boolean} [opts.loop=false]
     * @returns {{stopped:boolean, ended:boolean, source:AudioBufferSourceNode|null, gain:GainNode|null, _pendingStopMs:number}}
     */
    playMusic(src, opts = {}) {
        const volume = clamp01(opts.volume != null ? opts.volume : 1);
        const fadeInSec = Math.max(0, Number(opts.fadeInSec) || 0);
        const loop = !!opts.loop;
        const handle = { stopped: false, ended: false, source: null, gain: null, _pendingStopMs: 0 };
        if (this._destroyed || !src) { handle.stopped = true; handle.ended = true; return handle; }
        const ctx = this._ensureCtx();
        if (!ctx) { handle.stopped = true; handle.ended = true; return handle; }

        (async () => {
            const buf = await this.loadBuffer(src);
            if (!buf || this._destroyed || handle.stopped) { handle.ended = true; return; }
            if (ctx.state === 'suspended') { try { ctx.resume(); } catch (_) {} }
            try {
                const now = ctx.currentTime;
                const src1 = ctx.createBufferSource();
                src1.buffer = buf;
                src1.loop = loop;
                const g = ctx.createGain();
                if (fadeInSec > 0) {
                    // Ramp from 0 to target volume so a cross-fade
                    // start doesn't pop. The audio thread owns the
                    // envelope after start(), so frame drops can't
                    // hitch the fade.
                    g.gain.setValueAtTime(0, now);
                    g.gain.linearRampToValueAtTime(volume, now + fadeInSec);
                } else {
                    g.gain.value = volume;
                }
                src1.connect(g);
                g.connect(this._master);
                src1.start(now);
                this._sources.set(src1, g);
                handle.source = src1;
                handle.gain = g;
                const cleanup = () => {
                    this._sources.delete(src1);
                    handle.ended = true;
                    try { src1.disconnect(); g.disconnect(); } catch (_) {}
                };
                src1.onended = cleanup;
                // Looping music has no natural end — onended will
                // fire only when the caller stops the source via
                // stopHandle, so no safety timeout is needed.
                if (handle._pendingStopMs > 0) this.stopHandle(handle, handle._pendingStopMs);
            } catch (err) {
                console.warn('[NoteAudioPlayer] playMusic failed:', err?.message || err);
                handle.ended = true;
            }
        })();
        return handle;
    }

    _armGesture() {
        if (typeof window === 'undefined' || this._gestureHandler) return;
        const handler = () => {
            this._disarmGesture();
            try { this._ctx?.resume?.(); } catch (_) {}
        };
        this._gestureHandler = handler;
        window.addEventListener('pointerdown', handler, { once: true });
        window.addEventListener('keydown', handler, { once: true });
        window.addEventListener('touchstart', handler, { once: true });
    }

    _disarmGesture() {
        if (typeof window === 'undefined' || !this._gestureHandler) return;
        const h = this._gestureHandler;
        window.removeEventListener('pointerdown', h);
        window.removeEventListener('keydown', h);
        window.removeEventListener('touchstart', h);
        this._gestureHandler = null;
    }

    /** Stop every scheduled source + release the AudioContext. */
    destroy() {
        this._destroyed = true;
        this._disarmGesture();
        for (const [src, gain] of this._sources) {
            try { src.stop(0); } catch (_) {}
            try { src.disconnect(); } catch (_) {}
            try { gain.disconnect(); } catch (_) {}
        }
        this._sources.clear();
        this._buffers.clear();
        this._pending.clear();
        if (this._ctx) {
            try { this._ctx.close(); } catch (_) {}
            this._ctx = null;
            this._master = null;
        }
    }
}

function clamp01(v) {
    const n = Number(v);
    if (!Number.isFinite(n)) return 1;
    return n < 0 ? 0 : n > 1 ? 1 : n;
}

function clamp(v, lo, hi) {
    if (!Number.isFinite(v)) return lo;
    return v < lo ? lo : v > hi ? hi : v;
}

/**
 * Ramp `gain.gain` to silence over `fadeSec` starting NOW, robustly
 * across whatever automation is currently in flight on the AudioParam.
 *
 * The naive pattern — `cancelScheduledValues(now)` followed by
 * `setValueAtTime(gain.value, now)` — is RACY in Chrome: reading
 * `.value` mid-automation can return either the current automated
 * value OR the most-recently-set-value-at-time depending on engine
 * state, and `cancelScheduledValues` can snap the parameter back to
 * the previous setValueAtTime point. The combination produces a
 * very audible "blip back up to v then ramp down again" when a
 * Stop zone fires DURING a long fade-out tail.
 *
 * `cancelAndHoldAtTime(now)` (Web Audio standard, shipped Chrome 57+,
 * Firefox 70+, Safari 14.1+) is the correct primitive: it cancels
 * future automation AND pins the parameter to whatever value the
 * audio thread has computed at `now`. Then we just append a linear
 * ramp to 0. No race, no read of `.value`, no snap-back.
 *
 * Falls back to the legacy cancel-set-ramp pattern when the host
 * doesn't expose cancelAndHoldAtTime so we still degrade gracefully
 * on very old browsers.
 *
 * @param {GainNode} gain
 * @param {AudioContext} ctx
 * @param {number} fadeSec  Duration of the ramp in seconds.
 */
function _rampGainToZero(gain, ctx, fadeSec) {
    if (!gain || !ctx) return;
    const now = ctx.currentTime;
    const end = now + Math.max(0.005, Number(fadeSec) || 0.005);
    try {
        if (typeof gain.gain.cancelAndHoldAtTime === 'function') {
            gain.gain.cancelAndHoldAtTime(now);
        } else {
            const cur = Number(gain.gain.value) || 0;
            gain.gain.cancelScheduledValues(now);
            gain.gain.setValueAtTime(cur, now);
        }
        gain.gain.linearRampToValueAtTime(0, end);
    } catch (_) {
        try { gain.gain.value = 0; } catch (__) {}
    }
}
